'use client';

import React, { useEffect, useState } from 'react';
import MetricCard from '@/components/ui/MetricCard';
import {
  Wallet,
  PiggyBank,
  ArrowUpRight,
  Scale,
  TrendingUp,
  Clock,
  CreditCard,
  Users,
  AlertTriangle,
} from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

const formatVT = (amount: number) =>
  `VT ${amount.toLocaleString('en-US')}`;

interface KPIs {
  availableCash: number;
  totalCapitalPool: number;
  moneyLentOut: number;
  outstandingBalance: number;
  interestEarned: number;
  expectedInterest: number;
  activeLoans: number;
  borrowers: number;
  overdueLoans: number;
}

export default function DashboardKPIs() {
  const [kpis, setKpis] = useState<KPIs>({
    availableCash: 0,
    totalCapitalPool: 0,
    moneyLentOut: 0,
    outstandingBalance: 0,
    interestEarned: 0,
    expectedInterest: 0,
    activeLoans: 0,
    borrowers: 0,
    overdueLoans: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchKPIs = async () => {
      const supabase = createClient();
      const today = new Date().toISOString().split('T')[0];

      const [loansResult, borrowersResult, contributionsResult] = await Promise.all([
        supabase.from('loans').select('id, status, principal_amount, outstanding_balance, next_due_date'),
        supabase.from('borrowers').select('id', { count: 'exact', head: true }),
        supabase.from('capital_contributions').select('amount'),
      ]);

      const loans = loansResult.data || [];
      const borrowerCount = borrowersResult.count || 0;
      const contributions = contributionsResult.data || [];

      const activeLoans = loans.filter((l: any) => l.status === 'active');
      const overdueLoans = activeLoans.filter((l: any) => l.next_due_date && l.next_due_date < today);
      const totalCapitalPool = contributions.reduce((s: number, c: any) => s + Number(c.amount), 0);
      const moneyLentOut = activeLoans.reduce((s: number, l: any) => s + Number(l.principal_amount || 0), 0);
      const outstandingBalance = activeLoans.reduce((s: number, l: any) => s + Number(l.outstanding_balance || 0), 0);
      const availableCash = totalCapitalPool - moneyLentOut;

      setKpis({
        availableCash: Math.max(0, availableCash),
        totalCapitalPool,
        moneyLentOut,
        outstandingBalance,
        interestEarned: 0,
        expectedInterest: 0,
        activeLoans: activeLoans.length,
        borrowers: borrowerCount,
        overdueLoans: overdueLoans.length,
      });
      setLoading(false);
    };

    fetchKPIs();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 9 }).map((_, i) => (
          <div key={i} className={`${i === 0 ? 'col-span-2' : ''} bg-card border border-border rounded-xl p-4 animate-pulse h-24`} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
      {/* Hero: Available Cash — spans 2 cols */}
      <div className="col-span-2 md:col-span-2">
        <MetricCard
          label="Available Cash"
          value={formatVT(kpis.availableCash)}
          subValue="Ready to deploy for new loans"
          icon={<Wallet size={20} />}
          variant="positive"
          size="hero"
          className="h-full"
        />
      </div>

      {/* Total Capital Pool */}
      <MetricCard
        label="Total Capital Pool"
        value={formatVT(kpis.totalCapitalPool)}
        subValue="From investor contributions"
        icon={<PiggyBank size={18} />}
        variant="default"
      />

      {/* Money Lent Out */}
      <MetricCard
        label="Money Lent Out"
        value={formatVT(kpis.moneyLentOut)}
        subValue={kpis.totalCapitalPool > 0 ? `${((kpis.moneyLentOut / kpis.totalCapitalPool) * 100).toFixed(1)}% of pool deployed` : '0% of pool deployed'}
        icon={<ArrowUpRight size={18} />}
        variant="info"
      />

      {/* Outstanding Balance */}
      <MetricCard
        label="Outstanding Balance"
        value={formatVT(kpis.outstandingBalance)}
        subValue="Total borrower obligations"
        icon={<Scale size={18} />}
        variant="default"
      />

      {/* Interest Earned */}
      <MetricCard
        label="Interest Earned (MTD)"
        value={formatVT(kpis.interestEarned)}
        subValue="Realized this month"
        icon={<TrendingUp size={18} />}
        variant="positive"
      />

      {/* Expected Interest */}
      <MetricCard
        label="Expected Interest"
        value={formatVT(kpis.expectedInterest)}
        subValue="From active loan portfolio"
        icon={<Clock size={18} />}
        variant="default"
      />

      {/* Active Loans */}
      <MetricCard
        label="Active Loans"
        value={kpis.activeLoans.toString()}
        subValue={`Across ${kpis.borrowers} borrowers`}
        icon={<CreditCard size={18} />}
        variant="default"
      />

      {/* Borrowers */}
      <MetricCard
        label="Borrowers"
        value={kpis.borrowers.toString()}
        subValue="Total registered borrowers"
        icon={<Users size={18} />}
        variant="default"
      />

      {/* Overdue Loans — alert state */}
      <MetricCard
        label="Overdue Loans"
        value={kpis.overdueLoans.toString()}
        subValue={kpis.overdueLoans === 0 ? 'No overdue loans' : `${kpis.overdueLoans} loan${kpis.overdueLoans > 1 ? 's' : ''} past due`}
        icon={<AlertTriangle size={18} />}
        variant={kpis.overdueLoans > 0 ? 'negative' : 'positive'}
      />
    </div>
  );
}